import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import GridTable from "../../components/GridTable";
import { GridColDef } from "@mui/x-data-grid";
import Grid from '@mui/material/Grid';
import { GridCellParams } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import bcrypt from 'bcryptjs';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TextField from '../../components/TextField';
import Dialog from "../../components/Dialog";
import Loader from '../../components/Loader'
import Button from '../../components/Button'
import Dropdown from '../../components/Dropdown'
import ActionMenu from '../../components/ActionMenu';
import { successMessage, errorMessage } from '../../components/Toaster';
import { getAllServiceUsers, createServiceUser, updateServiceUser, updateServiceUserStatus, deleteServiceUser } from './api'
import { ApiResponse, ServiceUserData, CreateServiceUserParams } from './interface'
import { getAllRoles } from "../role/api";
import { RoleData } from '../role/interface';
import { getErrorMessage } from '../../utils/error'

const User: React.FC = () => {
    const [users, setUsers] = useState<ServiceUserData[]>([]);
    const [roles, setRoles] = useState<RoleData[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [editUser, setEditUser] = useState<ServiceUserData | null>(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<GridCellParams | null>(null);
    const [loading, setLoading] = useState({
        create: false,
        fetch: false,
        delete: false,
        update: false,
    });

    const validationSchema = Yup.object().shape({
        firstName: Yup.string().required("First Name is required"),
        lastName: Yup.string().required("Last Name is required"),
        userName: Yup.string().required("User Name is required"),
        role: Yup.string().required('Role is required'),
        password: Yup.string().when("editUser", {
            is: () => !editUser,
            then: (schema) => schema.required("Password is required"),
            otherwise: (schema) => schema,
        }),
        languagePreference: Yup.string().required("Language Preference is required"),
        phoneNumber: Yup.string()
            .when('email', {
                is: (email: string | null) => Boolean(email),
                then: (schema) => schema.nullable(),
                otherwise: (schema) =>
                    schema
                        .required('Either Email or Phone Number is required')
                        .matches(/^\d{10}$/, 'Phone Number must be exactly 10 digits')
            }),
        email: Yup.string()
            .email('Invalid email format')
            .nullable(),
    });

    const columns: GridColDef[] = [
        { field: "first_name", headerName: "First Name", flex: 1 },
        { field: "last_name", headerName: "Last Name", flex: 1 },
        { field: "user_name", headerName: "User Name", flex: 1 },
        { field: "role_name", headerName: "Role", flex: 1 },
        {
            field: "contact_info",
            headerName: "Phone/Email",
            flex: 1,
            renderCell: (params: GridCellParams) => {
                const phoneNumber = params.row?.phone_number;
                const email = params.row?.email;
                return <div>{phoneNumber || email || "N/A"}</div>;
            },
        },
        {
            field: "is_active",
            headerName: "Is Active",
            flex: 1,
            renderCell: (params: GridCellParams) => {
                const isActive = params.row?.is_active;
                const serviceUserId = params.row?.service_user_id;
                return (
                    <Button
                        label={isActive ? "Active" : "Inactive"}
                        color={isActive ? "success" : "error"}
                        variant="contained"
                        size="small"
                        onClick={() => UpdateStatus(serviceUserId, isActive)}
                    />
                );
            },
        },
        {
            field: "actions",
            headerName: "Actions",
            renderCell: (params: GridCellParams) => {
                return (
                    <ActionMenu
                        params={params}
                        actions={[
                            {
                                icon: <EditIcon color="primary" />,
                                tooltip: "Edit",
                                onClick: handleEdit,
                            },
                            {
                                icon: <DeleteIcon color="error" />,
                                tooltip: "Delete",
                                onClick: handleDelete,
                            },
                        ]}
                    />
                );
            },
            sortable: false,
            filterable: false,
            flex: 1,
        }
    ];

    useEffect(() => {
        const fetchUsers = async () => {
            setLoading((prev) => ({ ...prev, fetch: true }));
            try {
                const usersData: ApiResponse<ServiceUserData[]> = await getAllServiceUsers();
                if (usersData.status && usersData.data) {
                    setUsers(usersData.data);
                } else {
                    errorMessage(usersData.message || "Failed to fetch users.");
                }
            } catch (error) {
                console.error("Error fetching users:", error);
                const message = getErrorMessage(error);
                errorMessage(message || "Error fetching users");
            } finally {
                setLoading((prev) => ({ ...prev, fetch: false }));
            }
        };

        fetchUsers();
    }, []);


    const handleEdit = (params: GridCellParams) => {
        setEditUser(params.row as ServiceUserData);
        openDialog();
    };

    const handleDelete = (params: GridCellParams) => {
        setItemToDelete(params);
        setDeleteOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteOpen(false);
        setItemToDelete(null);
    };

    const UpdateStatus = async (serviceUserId: string, currentStatus: boolean) => {
        setLoading((prev) => ({ ...prev, fetch: true }));
        try {
            const updatedData = {
                service_user_id: serviceUserId,
                is_active: !currentStatus,
            };
            const response = await updateServiceUserStatus(updatedData);
            if (response.status) {
                successMessage(response.message || "User status updated.");
                setUsers((prevUsers) =>
                    prevUsers.map((user) =>
                        user.service_user_id === serviceUserId
                            ? { ...user, is_active: !currentStatus }
                            : user
                    )
                );
            } else {
                errorMessage(response.message || "Failed to update user status.");
            }
        } catch (error) {
            console.error("Error updating user status:", error);
            const message = getErrorMessage(error);
            errorMessage(message || "An unexpected error occurred.");
        } finally {
            setLoading((prev) => ({ ...prev, fetch: false }));
        }
    };

    const openDialog = async () => {
        setDialogOpen(true);
        setLoading((prev) => ({ ...prev, fetch: true }));
        try {
            const rolesRes: ApiResponse<RoleData[]> = await getAllRoles();
            if (rolesRes.status && rolesRes.data) {
                setRoles(rolesRes.data);
            } else {
                errorMessage(rolesRes.message || "Failed to fetch roles.");
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            const message = getErrorMessage(error);
            errorMessage(message || "An unexpected error occurred. Please try again.");
        } finally {
            setLoading((prev) => ({ ...prev, fetch: false }));
        }
    };

    const closeDialog = (resetForm: () => void) => {
        resetForm();
        setDialogOpen(false);
        setEditUser(null);
    };

    const confirmDialog = async (
        values: any,
        { resetForm }: { resetForm: () => void }
    ) => {
        let success = false;
        if (editUser) {
            success = await handleUpdate(values);
        } else {
            success = await handleCreate(values);
        }
        if (success) {
            closeDialog(resetForm);
        }
    };

    const handleCreate = async (values: any) => {
        setLoading((prev) => ({ ...prev, create: true }));
        try {
            const hashedPassword = await bcrypt.hash(values.password, 10);
            const newUser: CreateServiceUserParams = {
                first_name: values.firstName.trim(),
                last_name: values.lastName.trim(),
                user_name: values.userName.trim(),
                password: hashedPassword,
                language_preference: values.languagePreference,
                phone_number: values?.phoneNumber?.trim(),
                email: values?.email?.trim(),
                role_name: values?.role
            };

            const response = await createServiceUser(newUser);
            if (response?.status) {
                setUsers((prevUsers) => {
                    const currentUsers = Array.isArray(prevUsers) ? prevUsers : [];
                    return [
                        ...currentUsers,
                        {
                            service_user_id: response?.data?.service_user_id || "",
                            first_name: values.firstName,
                            last_name: values.lastName,
                            user_name: values.userName,
                            phone_number: values.phoneNumber,
                            email: values.email,
                            language_preference: values.languagePreference,
                            role_name: values?.role,
                            is_active: "TRUE"
                        }
                    ];
                });
                successMessage(response.message || "User created successfully!");
                return true;
            } else {
                errorMessage(response?.message || "Failed to create user. Please try again.");
                return false;
            }
        } catch (error) {
            console.error("Error creating user:", error);
            const message = getErrorMessage(error);
            errorMessage(message || "An unexpected error occurred. Please try again.");
            return false;
        } finally {
            setLoading((prev) => ({ ...prev, create: false }));
        }
    };

    const handleUpdate = async (values: any) => {
        setLoading((prev) => ({ ...prev, update: true }));
        try {
            const updatedServiceUserData = {
                service_user_id: editUser!.service_user_id || "",
                first_name: values.firstName.trim(),
                last_name: values.lastName.trim(),
                user_name: values.userName.trim(),
                phone_number: values?.phoneNumber?.trim(),
                email: values?.email?.trim(),
                role_name: values?.role
            };

            const response = await updateServiceUser(updatedServiceUserData);
            if (response.status) {
                const updatedUser = updatedServiceUserData;
                setUsers((prevUsers) =>
                    prevUsers.map((user) =>
                        user.service_user_id === updatedUser.service_user_id
                            ? { ...user, ...updatedUser }
                            : user
                    )
                );
                successMessage(response.message || "User updated successfully!");
                return true;
            } else {
                errorMessage(response.message || "Failed to update user. Please try again.");
                return false;
            }
        } catch (error) {
            console.error("Error updating user:", error);
            const message = getErrorMessage(error);
            errorMessage(message || "An unexpected error occurred. Please try again.");
            return false;
        } finally {
            setLoading((prev) => ({ ...prev, update: false }));
        }
    };

    const handleDeleteConfirm = async () => {
        setLoading((prev) => ({ ...prev, delete: true }));

        if (itemToDelete) {
            try {
                const response = await deleteServiceUser(itemToDelete.row.service_user_id);

                if (response.status) {
                    setUsers((prevUsers) =>
                        prevUsers.filter((user) => user.service_user_id !== itemToDelete.row.service_user_id)
                    );
                    successMessage(response.message || "User deleted successfully!");
                } else {
                    errorMessage(response.message || "Failed to delete user. Please try again.");
                }
            } catch (error) {
                console.error("Error deleting user:", error);
                const message = getErrorMessage(error);
                errorMessage(message || "An unexpected error occurred. Please try again.");
            } finally {
                setLoading((prev) => ({ ...prev, delete: false }));
                setDeleteOpen(false);
            }
        }
    };

    return (
        <>
            {loading.fetch ? (
                <Loader color="primary" size={40} />
            ) : (
                <Box style={{ width: '100%', height: '50%' }}>
                    <main className="m-a-20">
                        <div>
                            <div className='d-flex justify-end'>
                                <div className='d-flex justify-between w-100'>
                                    <TextField
                                        className='w-25'
                                        label="Search"
                                        variant="outlined"
                                        value={searchQuery}
                                        size="small"
                                        startIcon={<SearchIcon />}
                                        onChange={(e) => setSearchQuery(e.target.value)} />
                                    <Button
                                        label="Add"
                                        variant="contained"
                                        color="primary"
                                        onClick={openDialog}
                                    />
                                </div>
                            </div>
                        </div>
                        <div style={{ height: '100%', width: '100%' }} className="ag-theme-alpine m-t-20">
                            <GridTable
                                rows={users}
                                columns={columns}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                getRowId={(row) => row.user_name}
                                density="standard"
                            />
                        </div>
                        <Formik
                            initialValues={{
                                firstName: editUser ? editUser.first_name : "",
                                lastName: editUser ? editUser.last_name : "",
                                userName: editUser ? editUser.user_name : "",
                                phoneNumber: editUser ? editUser.phone_number : null,
                                email: editUser ? editUser.email : null,
                                password: editUser ? "" : "",
                                languagePreference: editUser ? editUser.language_preference : "EN",
                                role: editUser ? editUser.role_name : "",
                            }}
                            validationSchema={validationSchema}
                            onSubmit={confirmDialog}
                            enableReinitialize
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleChange,
                                handleSubmit,
                                resetForm,
                                setFieldValue
                            }) => (
                                <>
                                    <Dialog
                                        open={isDialogOpen}
                                        onClose={() => closeDialog(resetForm)}
                                        onConfirm={handleSubmit}
                                        title={
                                            !loading.update && !loading.create
                                                ? editUser
                                                    ? "Edit User"
                                                    : "Add New User"
                                                : ""
                                        }
                                        confirmButtonText={
                                            !loading.update && !loading.create
                                                ? editUser
                                                    ? "Update"
                                                    : "Add"
                                                : ""
                                        }
                                        cancelButtonText={
                                            !loading.update && !loading.create ? "Cancel" : ""
                                        }
                                        icon={<PersonAddIcon />}
                                        message={
                                            <Box padding={4}>
                                                {loading.create ? (
                                                    <Loader
                                                        color="primary"
                                                        size={40}
                                                    />
                                                ) : (
                                                    <Form>
                                                        <Grid container spacing={2}>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="firstName"
                                                                    name="firstName"
                                                                    label="First Name"
                                                                    required
                                                                    value={values.firstName}
                                                                    onChange={handleChange}
                                                                    error={touched.firstName && Boolean(errors.firstName)}
                                                                    helperText={touched.firstName && errors.firstName}
                                                                />
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="lastName"
                                                                    name="lastName"
                                                                    label="Last Name"
                                                                    required
                                                                    value={values.lastName}
                                                                    onChange={handleChange}
                                                                    error={touched.lastName && Boolean(errors.lastName)}
                                                                    helperText={touched.lastName && errors.lastName}
                                                                />
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="userName"
                                                                    name="userName"
                                                                    label="User Name"
                                                                    required
                                                                    value={values.userName}
                                                                    onChange={handleChange}
                                                                    error={touched.userName && Boolean(errors.userName)}
                                                                    helperText={touched.userName && errors.userName}
                                                                />
                                                            </Grid>
                                                            {!editUser &&
                                                                <Grid size={4}>
                                                                    <TextField
                                                                        id="password"
                                                                        name="password"
                                                                        label="Password"
                                                                        type="password"
                                                                        value={values.password}
                                                                        onChange={handleChange}
                                                                        error={touched.password && Boolean(errors.password)}
                                                                        helperText={touched.password && errors.password}
                                                                    />
                                                                </Grid>}
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="phoneNumber"
                                                                    name="phoneNumber"
                                                                    label="Phone Number"
                                                                    value={values.phoneNumber ?? ""}
                                                                    onChange={handleChange}
                                                                    error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                                                                    helperText={touched.phoneNumber && errors.phoneNumber}
                                                                />
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="email"
                                                                    name="email"
                                                                    label="Email"
                                                                    value={values.email ?? ""}
                                                                    onChange={handleChange}
                                                                    error={touched.email && Boolean(errors.email)}
                                                                    helperText={touched.email && errors.email}
                                                                />
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <Dropdown
                                                                    options={roles?.map(
                                                                        (item) => `${item?.role_name}`
                                                                    )}
                                                                    value={values.role}
                                                                    label="Role"
                                                                    onChange={(option) =>
                                                                        setFieldValue("role", option)
                                                                    }
                                                                    required
                                                                    error={Boolean(touched.role && errors.role)}
                                                                    helperText={
                                                                        touched.role && errors.role
                                                                            ? String(errors.role)
                                                                            : undefined
                                                                    }
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Form>
                                                )}
                                            </Box>
                                        }
                                    />
                                </>
                            )}
                        </Formik>
                        <Dialog
                            open={deleteOpen}
                            onClose={handleDeleteCancel}
                            onConfirm={handleDeleteConfirm}
                            title={!loading.delete ? "Delete User" : ""}
                            confirmButtonText={!loading.delete ? "Confirm" : ""}
                            cancelButtonText={!loading.delete ? "Cancel" : ""}
                            icon={<DeleteIcon />}
                            message={
                                <Box padding={2}>
                                    {loading.delete ? (
                                        <Loader
                                            color="primary"
                                            size={40}
                                        />
                                    ) : (
                                        "Are you sure you want to delete this user?"
                                    )}
                                </Box>
                            }
                        />
                    </main>
                </Box>
            )}
        </>
    );
};

export default User;
